import React, { DetailedHTMLProps, InputHTMLAttributes } from "react";

import classnames from "classnames";
import { FieldError } from "react-hook-form";

import { useWombatTheme } from "../../../theme";
import { ErrorIcon } from "../../../../icons";

/**
 * Input size must be used as an affordance:
 * These are sensible default that should cover most use cases.
 * For example:
 *  - For first names, last names, email, ... use small
 *  - For titles, ... use medium
 *  - For adresses, ... use large
 */
export type InputSize = "xsmall" | "small" | "medium" | "large";

export type InputType = "text" | "email" | "password" | "number";

export type InputProps = {
  /**
   * Label
   */
  label: string;
  /**
   * Define if the label shoulb only be seen on screen reader
   */
  srOnlyLabel?: boolean;
  /**
   * Input id, the component will automatically infer all data-test-id from it.
   *
   * data-test-id strategy:
   *  - Container: input_${id}_group
   *  - Label: input_${id}_label
   *  - Description: input_${id}_description
   *  - Input: input_${id}
   *  - Help text: input_${id}_help_message
   *  - Error message: input_${id}_error_message
   *
   * It will also do the matching from the label to the input in respect to accessibility patterns
   */
  id: string;
  /**
   * Helper text
   * @default undefined
   */
  help?: string;
  /**
   * Description of the field
   * @default undefined
   */
  description?: string;
  /**
   * Refer to InputSize type description to get a sense of how to use it
   * If unset, the input element will fill the entire width of his container
   */
  size?: InputSize;
  /**
   * The react-hook-form error field
   * @default {}
   */
  error?: FieldError;
  /**
   * The input type
   * @default 'text'
   */
  type?: InputType;
  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
} & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "type" | "size"
>;

/**
 * Input are meant to be used in a constrained space.
 * Sensible defaults are provided through the size props
 * When unset the input will take up all available width of his parent container.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      help,
      description,
      size,
      type = "text",
      id,
      srOnlyLabel = false,
      error = {},
      disabled = false,
      ...props
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const containerStyle = classnames(
      "relative rounded-md shadow-sm",
      size && theme.form.input.size[size]
    );
    const labelStyle = classnames(
      theme.form.input.label.base,
      disabled || props.readOnly ? theme.form.input.label.disabled : theme.form.input.label.able,
      srOnlyLabel && "sr-only"
    );
    const descriptionStyle = classnames(theme.form.input.description);
    const inputStyle = classnames(
      theme.form.input.base,
      theme.form.input.status[error?.message ? "error" : "default"],
      size && theme.form.input.size[size]
    );

    const iconStyle = classnames(theme.form.input.icon.error);
    const helpStyle = classnames(
      theme.form.input.help[error?.message ? "error" : "default"]
    );

    return (
      <div data-test-id={`input_${id}_group`}>
        {label && (
          <label
            className={labelStyle}
            htmlFor={id}
            data-test-id={`input_${id}_label`}
          >
            {label}
            {description && <p className={descriptionStyle}>{description}</p>}
          </label>
        )}
        <div className={containerStyle}>
          <input
            {...props}
            type={type}
            ref={ref}
            disabled={disabled}
            aria-disabled={disabled ? "true" : "false"}
            className={inputStyle}
            data-test-id={`input_${id}`}
            id={id}
          />
          {error?.message && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ErrorIcon className={iconStyle} />
            </div>
          )}
        </div>
        {(help || error?.message) && (
          <p
            className={helpStyle}
            data-test-id={`input_${id}_${
              error?.message ? "error_message" : "help_message"
            }`}
          >
            {error?.message ? error?.message : help}
          </p>
        )}
      </div>
    );
  }
);

export default React.memo(Input);
